import React from 'react';
import Paper from '@mui/material/Paper';
import { BarChart } from '@mui/x-charts/BarChart';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { type MonthComparablesGMVResponse } from 'api/admin/businesses/barry/index';
import { useGetActiveMembershipCustomers } from 'api/queries/barry/useGetActiveMembershipCustomers';
import { useGetActivePromotionCount } from 'api/queries/barry/useGetActivePromotionCount';
import { useGetDropOffGMV } from 'api/queries/barry/useGetDropOffGMV';
import { useGetInvoiceGMV } from 'api/queries/barry/useGetInvoiceGMV';
import { useGetOwnDriverDeliveryGMV } from 'api/queries/barry/useGetOwnDriverDeliveryGMV';
import { useGetSelfServeGMV } from 'api/queries/barry/useGetSelfServeGMV';
import { useGetThirdPartyDeliveryGMV } from 'api/queries/barry/useGetThirdPartyDeliveryGMV';
import styles from './GMVData.module.scss';

interface GMVDataProps {
  businessId: number;
}

interface ChartConfig {
  label: string;
  dataKeys: {
    lastMonth: keyof MonthComparablesGMVResponse['results'];
    currentMonth: keyof MonthComparablesGMVResponse['results'];
  };
  color?: string;
}

const ChartSection = ({
  title,
  data,
  isLoading,
  charts,
}: {
  title: string;
  data?: MonthComparablesGMVResponse['results'];
  isLoading: boolean;
  charts: ChartConfig[];
}) => {
  const defaultChartColors = ['#3790f4', '#f44336', '#4caf50', '#ff9800', '#9c27b0'];

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <CentsLoader />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={styles.chartSectionContainer}>
        <h4>{title}</h4>
        <div className={styles.cardRow}>
          <div className={styles.cardItem}>
            <Paper className={styles.card}>
              <h5>Order Count Percentage Change</h5>
              <p className={styles.centerTextAlign}>
                {data.order_count_percent_change}%
              </p>
            </Paper>
          </div>
          <div className={styles.cardItem}>
            <Paper className={styles.card}>
              <h5>Total Value Percentage Change</h5>
              <p className={styles.centerTextAlign}>
                {data.total_value_percent_change}%
              </p>
            </Paper>
          </div>
          <div className={styles.cardItem}>
            <Paper className={styles.card}>
              <h5>Payment Value Percentage Change</h5>
              <p className={styles.centerTextAlign}>
                {data.payments_percent_change}%
              </p>
            </Paper>
          </div>
        </div>
      {charts.map((chart, index) => (
        <div key={chart.label} className={styles.chartContainer}>
          <h5 className={styles.individualChartHeader}>{chart.label}</h5>
          <BarChart
            xAxis={[{ data: ['Last Month', 'Current Month'] }]}
            series={[
              {
                data: [
                  data[chart.dataKeys.lastMonth] as number,
                  data[chart.dataKeys.currentMonth] as number,
                ],
                label: chart.label,
              },
            ]}
            height={300}
            colors={[chart.color || defaultChartColors[index % defaultChartColors.length]]}
          />
        </div>
      ))}
    </div>
  );
};

export const GMVData = (props: GMVDataProps) => {
  const { businessId } = props;

  const { data: activeMembershipCustomersData, isLoading: isLoadingActiveMembershipCustomers } =
    useGetActiveMembershipCustomers(businessId, true);

  const { data: activePromotionCountData, isLoading: isLoadingActivePromotionCount } =
    useGetActivePromotionCount(businessId, true);

  const { data: dropOffGMVData, isLoading: isLoadingDropOffGMV } = useGetDropOffGMV(
    businessId,
    true,
  );

  const { data: invoiceGMVData, isLoading: isLoadingInvoiceGMV } = useGetInvoiceGMV(
    businessId,
    true,
  );

  const { data: ownDriverDeliveryGMVData, isLoading: isLoadingOwnDriverDeliveryGMV } =
    useGetOwnDriverDeliveryGMV(businessId, true);

  const { data: selfServeGMVData, isLoading: isLoadingSelfServeGMV } = useGetSelfServeGMV(
    businessId,
    true,
  );

  const { data: thirdPartyDeliveryGMVData, isLoading: isLoadingThirdPartyDeliveryGMV } =
    useGetThirdPartyDeliveryGMV(businessId, true);

  const showTopCards =
    !isLoadingActivePromotionCount &&
    !isLoadingActiveMembershipCustomers &&
    activePromotionCountData &&
    activeMembershipCustomersData;

  return (
    <div>
      {showTopCards && (
        <div className={styles.cardRow}>
          <div className={styles.cardItem}>
            <Paper className={styles.card}>
              <h4>Active Promotions</h4>
              <p className={styles.centerTextAlign}>
                {activePromotionCountData.active_promotion_count}
              </p>
            </Paper>
          </div>
          <div className={styles.cardItem}>
            <Paper className={styles.card}>
              <h4>Customers with Memberships</h4>
              <p className={styles.centerTextAlign}>
                {activeMembershipCustomersData.active_membership_customers}
              </p>
            </Paper>
          </div>
        </div>
      )}

      {(isLoadingActivePromotionCount || isLoadingActiveMembershipCustomers) && !showTopCards && (
        <div className={styles.cardRow}>
          <CentsLoader />
        </div>
      )}

      <ChartSection
        title="Drop Off GMV"
        data={dropOffGMVData}
        isLoading={isLoadingDropOffGMV}
        charts={[
          {
            label: 'Payments Collected',
            dataKeys: {
              lastMonth: 'last_month_payments_collected',
              currentMonth: 'current_month_payments_collected',
            },
            color: '#3790f4',
          },
          {
            label: 'Order Count',
            dataKeys: {
              lastMonth: 'last_month_order_count',
              currentMonth: 'current_month_order_count',
            },
            color: '#4caf50',
          },
          {
            label: 'Total Order Value',
            dataKeys: {
              lastMonth: 'last_month_total_value',
              currentMonth: 'current_month_total_value',
            },
            color: '#ff9800',
          },
        ]}
      />

      <ChartSection
        title="Third Party Delivery GMV"
        data={thirdPartyDeliveryGMVData}
        isLoading={isLoadingThirdPartyDeliveryGMV}
        charts={[
          {
            label: 'Payments Collected',
            dataKeys: {
              lastMonth: 'last_month_payments_collected',
              currentMonth: 'current_month_payments_collected',
            },
            color: '#3790f4',
          },
          {
            label: 'Order Count',
            dataKeys: {
              lastMonth: 'last_month_order_count',
              currentMonth: 'current_month_order_count',
            },
            color: '#4caf50',
          },
          {
            label: 'Total Order Value',
            dataKeys: {
              lastMonth: 'last_month_total_value',
              currentMonth: 'current_month_total_value',
            },
            color: '#ff9800',
          },
        ]}
      />

      <ChartSection
        title="Own Driver Delivery GMV"
        data={ownDriverDeliveryGMVData}
        isLoading={isLoadingOwnDriverDeliveryGMV}
        charts={[
          {
            label: 'Payments Collected',
            dataKeys: {
              lastMonth: 'last_month_payments_collected',
              currentMonth: 'current_month_payments_collected',
            },
            color: '#3790f4',
          },
          {
            label: 'Order Count',
            dataKeys: {
              lastMonth: 'last_month_order_count',
              currentMonth: 'current_month_order_count',
            },
            color: '#4caf50',
          },
          {
            label: 'Total Order Value',
            dataKeys: {
              lastMonth: 'last_month_total_value',
              currentMonth: 'current_month_total_value',
            },
            color: '#ff9800',
          },
        ]}
      />

      <ChartSection
        title="Self Serve GMV"
        data={selfServeGMVData}
        isLoading={isLoadingSelfServeGMV}
        charts={[
          {
            label: 'Payments Collected',
            dataKeys: {
              lastMonth: 'last_month_payments_collected',
              currentMonth: 'current_month_payments_collected',
            },
            color: '#3790f4',
          },
          {
            label: 'Order Count',
            dataKeys: {
              lastMonth: 'last_month_order_count',
              currentMonth: 'current_month_order_count',
            },
            color: '#4caf50',
          },
          {
            label: 'Total Order Value',
            dataKeys: {
              lastMonth: 'last_month_total_value',
              currentMonth: 'current_month_total_value',
            },
            color: '#ff9800',
          },
        ]}
      />

      <ChartSection
        title="Invoice GMV"
        data={invoiceGMVData}
        isLoading={isLoadingInvoiceGMV}
        charts={[
          {
            label: 'Payments Collected',
            dataKeys: {
              lastMonth: 'last_month_payments_collected',
              currentMonth: 'current_month_payments_collected',
            },
            color: '#3790f4',
          },
          {
            label: 'Order Count',
            dataKeys: {
              lastMonth: 'last_month_order_count',
              currentMonth: 'current_month_order_count',
            },
            color: '#4caf50',
          },
          {
            label: 'Total Order Value',
            dataKeys: {
              lastMonth: 'last_month_total_value',
              currentMonth: 'current_month_total_value',
            },
            color: '#ff9800',
          },
        ]}
      />
    </div>
  );
};
